import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { styles } from "@repo/helpers";
import { MouseEvent, useCallback, useEffect, useMemo, useState } from "react";

import { useValidationContext } from "../contexts/validation";
import { byOrdinal, isVisible } from "../helpers/sectionMapping";
import { componentType } from "../models/enums";
import { MappedComponent, MappedDyno, MappedSection } from "../models/mappedComponent";
import style from "./style.module.scss";

function sectionVisible(section: MappedSection) {
	const hasComponents = section.components.filter(isVisible).length;
	const hasChildSections = section.childSections.filter(isVisible).length;
	return section.visible && (hasComponents || hasChildSections);
}

export default function DynamicComponentListSection(props: Props) {
	const { section, index, sectionClicked, activeSection } = props;
	const { validationResults } = useValidationContext();
	const sortedSections = useMemo(() => (section.childSections || []).sort(byOrdinal), [section.childSections]);
	const sortedComponents = useMemo(
		() => (section.components || []).filter(isVisible).sort(byOrdinal),
		[section.components],
	);
	const visible = useMemo(
		() => sectionVisible(section),
		[section.visible, section.components, section.childSections],
	);
	const [active, setActive] = useState(props.active);

	const sectionName = props.topSectionName || section.linkName;

	useEffect(() => {
		const newActive = props.active || isSectionActive(section);
		if (newActive !== active) setActive(newActive);
	}, [props.active, activeSection, sectionName]);

	const subSectionClicked = useCallback(props.subSectionClicked(sectionName), [sectionName, props.subSectionClicked]);

	function isSectionActive(section: MappedSection) {
		const { activeSection } = props;
		if (activeSection === sectionName) return true;
		return section.childSections.some(isSectionActive);
	}

	if (!visible) return null;
	//this shoudl be reworked, but if a section is a subsection, should act like a component,
	//except it also has stuff under it
	const sectionStyle = styles(
		!props.topSectionName ? style.sectionSelector : style.subsections,
		active && style.active,
	);
	return (
		<div key={index}>
			<div
				data-subsectionfor={section.linkName}
				data-section={sectionName}
				className={sectionStyle}
				onClick={sectionClicked}
			>
				{renderLabel(section)}
			</div>
			{renderSubSections()}
		</div>
	);

	function renderSubSections() {
		const containerStyle = styles(style.subItemContainer, active && style.active);

		return (
			<div className={containerStyle}>
				{sortedComponents.map(renderComponent)}
				{sortedSections.map(renderSubSection)}
			</div>
		);
	}

	function renderSubSection(section: MappedSection, index: number) {
		return (
			<DynamicComponentListSection
				{...props}
				section={section}
				sectionClicked={subSectionClicked}
				topSectionName={sectionName}
				key={index}
			/>
		);
	}

	function renderComponent(component: MappedComponent, index: number) {
		const componentStyle = styles(style.components, style.subsectionSelector, active && style.active);
		const cmp = component.component;
		//Pure text stuff doesn't show up in menu
		if (cmp.type === componentType.template || cmp.type === componentType.text) return;
		return (
			<div
				key={index}
				data-subsectionfor={component.linkName}
				className={componentStyle}
				data-componenttype={component.type}
				onClick={subSectionClicked}
			>
				{renderLabel(component)}
			</div>
		);
	}

	function renderLabel(component: MappedDyno) {
		let error = validationResults.getError(component);
		if (!component.label) return null;
		if (!error) return component.label;
		error = error
			.split(",")
			.map((s) => s.trim())
			.join("\n");
		return (
			<div className={style.label}>
				<FontAwesomeIcon
					icon={faExclamationTriangle}
					title={error}
					color="orange"
					className={style.warningIcon}
				/>
				{component.label}
			</div>
		);
	}
}
interface Props {
	section: MappedSection;
	topSectionName?: string;
	active?: boolean;
	index: number;

	activeSection?: string;
	sectionClicked: (e: MouseEvent) => void;
	subSectionClicked: (sectionName: string) => (e: MouseEvent) => Promise<void>;
}
